import { Box, Flex, Heading, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import 'borrower/borrower.scss';

import { useGetUserActiveCreditFacilitiesQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { ProgressAlert } from 'packages/common-frontend/components/Alert';
import { LoanProductSelect } from 'packages/common-frontend/components/LoanProductSelect';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { CreditFacilitySelectWithUrlParams } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { ReceivablesTable } from 'packages/loanProduct/ui/receivablesDashboard/ReceivablesTable';
import { useAthenaSchemaContext } from 'packages/organization/hooks/AthenaSchemaContext';

export const CreditFacilityReceivablesDashboardRoute = withLDConsumer()(
  ({ flags }) => {
    const toast = useToast();
    const history = useHistory();
    const {
      AthenaSchemaContextLoadingOrErrorComponent,
    } = useAthenaSchemaContext();

    const [loanProductId, setLoanProductId] = useState<string>('all');

    const { creditFacilityId: creditFacilityIdParam } = useParams<{
      creditFacilityId: string;
    }>();

    const {
      creditFacilityId,
      setCreditFacilityId,
    } = useCreditFacilityContext();

    if (creditFacilityIdParam && creditFacilityId !== creditFacilityIdParam) {
      setCreditFacilityId(creditFacilityIdParam);
    }

    let routeLevelError = false;

    const { data: userData } = useGetUserActiveCreditFacilitiesQuery({
      variables: {
        creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
      },
    });

    if (!userData || !userData.user) {
      return <Skeleton data-testid="loading-receivables" height="600px" />;
    }

    const {
      user: {
        organizationId,
        organization: { creditFacilities },
      },
    } = userData;

    const creditFacility = _.find(
      creditFacilities,
      ({ id }) => id === creditFacilityId,
    );

    const creditFacilitySlug = creditFacility?.slug;

    if (
      !creditFacilityId ||
      !creditFacilitySlug ||
      !creditFacility.latestCreditAgreementId
    ) {
      history.push('/');
      toast({
        ...errorToastConfig,
        id: 'errorLoadReceivablesDashboardNoCreditFacility',
        title: 'Please select a credit facility.',
      });
      return null;
    }

    return (
      <Box className="main">
        <Sentry.ErrorBoundary
          onError={() => {
            if (!routeLevelError && !toast.isActive(errorToastId)) {
              routeLevelError = true;
              toast({
                ...errorToastConfig,
                id: 'errorLoadReceivables',
                title: 'Error loading borrower receivables dashboard route',
              });
              history.push('/');
            }
          }}
        >
          <Flex mb="24px" direction="column" w="50%">
            <Heading mb="24px">Receivables</Heading>
            <Box>
              <CreditFacilitySelectWithUrlParams
                organizationId={organizationId}
                displayInputFloatingLabel
                inputFloatingLabelBackgroundColor="white"
                onSelectRedirectLink={(selectedCreditFacilityId: string) => {
                  return `/credit-facility-receivables/${selectedCreditFacilityId}`;
                }}
              />
            </Box>
          </Flex>
          <AthenaSchemaContextLoadingOrErrorComponent
            renderLoading={
              <Box>
                <ProgressAlert>
                  Getting your portfolio ready. This may take a moment.
                </ProgressAlert>
              </Box>
            }
          >
            <Box w="50%">
              <LoanProductSelect
                creditFacilityId={creditFacilityId}
                loanProductId={loanProductId}
                setLoanProductId={setLoanProductId}
                useAllOption
              />
            </Box>

            <ReceivablesTable
              organizationId={organizationId}
              creditFacilitySlug={creditFacilitySlug}
              creditFacilityId={creditFacilityId}
              loanProductId={loanProductId ?? undefined}
              isLenderPage={false}
            />
          </AthenaSchemaContextLoadingOrErrorComponent>
        </Sentry.ErrorBoundary>
      </Box>
    );
  },
);
